import type { RulesConfig } from '@smart-link/shared';
import { parseRulesJson, serializeRulesConfig } from '@smart-link/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { JsonEditor } from '../components/JsonEditor';
import { RulesEditor } from '../components/RulesEditor';
import { useToast } from '../components/Toaster';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api, getToken } from '../lib/api';

export const Route = createFileRoute('/rules')({
  beforeLoad: () => {
    if (!getToken()) throw redirect({ to: '/login' });
  },
  component: RulesPage,
});

/** 表单 = 逐项编辑;JSON = 全量文本,用于跨环境(Beta / DC / RB)复制粘贴同步。 */
type EditMode = 'form' | 'json';

function RulesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data } = useQuery({
    queryKey: ['rules'],
    queryFn: () => api<RulesConfig>('/api/rules'),
  });
  const [draft, setDraft] = useState<RulesConfig | null>(null);
  const [mode, setMode] = useState<EditMode>('form');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (cfg: RulesConfig) =>
      api<{ ok: boolean }>('/api/rules', { method: 'PUT', body: JSON.stringify(cfg) }),
    onSuccess: (_res, cfg) => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      // 回显实际落库的值(schema 会丢弃本部署不认识的规则 id、补齐缺失字段),
      // 这样粘进来的内容与真正存下的内容不一致时,当场就能看出来。
      setJsonText(serializeRulesConfig(cfg));
      toast.success('保存成功', '新规则已生效，下一次访问立即应用');
    },
    onError: (e) =>
      toast.error('保存失败', e instanceof Error ? e.message : '请检查网络或重新登录'),
  });

  /**
   * 解析文本框内容并同步进 draft;失败时记下错误(就地提示 + toast,覆盖长文本
   * 滚动后错误不在视野内的情况)并返回 null。切换与保存共用这一条路径。
   */
  const commitJsonText = (failureTitle: string): RulesConfig | null => {
    const parsed = parseRulesJson(jsonText);
    if (!parsed.ok) {
      setJsonError(parsed.error);
      toast.error(failureTitle, parsed.error);
      return null;
    }
    setJsonError(null);
    setDraft(parsed.config);
    return parsed.config;
  };

  /** JSON → 表单必须先解析成功,否则不切走(切走等于丢掉编辑中的文本)。 */
  const switchMode = (next: EditMode) => {
    if (!draft || next === mode) return;
    if (next === 'json') {
      setJsonText(serializeRulesConfig(draft));
      setJsonError(null);
      setMode('json');
    } else if (commitJsonText('JSON 有误，无法切回表单')) {
      setMode('form');
    }
  };

  const handleSave = () => {
    if (!draft) return;
    // JSON 模式以文本框为准(可能刚粘贴、还没切回表单)。
    const cfg = mode === 'form' ? draft : commitJsonText('JSON 有误，未保存');
    if (cfg) save.mutate(cfg);
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(jsonText);
      toast.success('已复制', '切换数据环境后粘贴到 JSON 编辑框');
    } catch {
      toast.error('复制失败', '请手动全选复制');
    }
  };

  if (!draft) return <div className="text-muted-foreground p-6 text-sm">加载中…</div>;

  const saveButton = (
    <Button onClick={handleSave} disabled={save.isPending}>
      {save.isPending ? '保存中…' : '保存'}
    </Button>
  );

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">平台默认规则</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            所有链接继承此配置，各链接可单独覆盖。保存后立即生效。
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Tabs value={mode} onValueChange={(v) => switchMode(v as EditMode)}>
            <TabsList>
              <TabsTrigger value="form">表单</TabsTrigger>
              <TabsTrigger value="json">JSON</TabsTrigger>
            </TabsList>
          </Tabs>
          {saveButton}
        </div>
      </div>

      {mode === 'form' ? (
        <RulesEditor value={draft} onChange={setDraft} />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm">
              全量配置。复制后切换左下角数据环境，粘贴到此处保存即可同步。
            </p>
            <Button variant="outline" size="sm" onClick={copyJson} className="shrink-0">
              复制
            </Button>
          </div>
          <JsonEditor
            value={jsonText}
            onChange={(next) => {
              setJsonText(next);
              setJsonError(null);
            }}
            invalid={!!jsonError}
            className="h-[60vh]"
          />
          {jsonError && <p className="text-sm text-red-600 dark:text-red-400">{jsonError}</p>}
        </div>
      )}

      <div className="mt-6 flex justify-end">{saveButton}</div>
    </div>
  );
}
